/**
 * App Layout Component
 * Main layout with navbar and sidebar for authenticated pages
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Container,
  Badge,
  Snackbar,
  Alert,
  Button,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import MenuIcon from '@mui/icons-material/Menu';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import MasLogo from '../../assets/Mas_logo.png';

const DRAWER_WIDTH = 240;
// How often admins check for new sign-ups
const PENDING_POLL_MS = 60000;

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, refreshUser } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  // Initialise from sessionStorage immediately so navigation never shows "Never"
  const [lastSync, setLastSync] = useState(() => {
    const stored = sessionStorage.getItem('etl_last_sync');
    return stored ? new Date(stored) : null;
  });
  const [, setTick] = useState(0);

  const updateLastSync = (date) => {
    setLastSync(date);
    if (date) sessionStorage.setItem('etl_last_sync', date.toISOString());
  };

  useEffect(() => {
    const fetchSyncStatus = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch('/api/dashboard/sync-status', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.last_sync) {
            const dbTime = new Date(data.last_sync);
            // Only update if DB timestamp is newer than what we already show
            // — prevents "49 min ago" from overwriting a fresh "Just now"
            const stored = sessionStorage.getItem('etl_last_sync');
            const current = stored ? new Date(stored) : null;
            if (!current || dbTime > current) {
              updateLastSync(dbTime);
            }
          }
        }
      } catch (_) {}
    };
    fetchSyncStatus();

    // Tick every 60s to keep "X min ago" live without re-fetching
    const tickId = setInterval(() => setTick(t => t + 1), 60000);

    const onEtlStarted = () => updateLastSync(new Date());
    const onEtlCompleted = () => updateLastSync(new Date());

    window.addEventListener('etl:started', onEtlStarted);
    window.addEventListener('etl:completed', onEtlCompleted);

    return () => {
      clearInterval(tickId);
      window.removeEventListener('etl:started', onEtlStarted);
      window.removeEventListener('etl:completed', onEtlCompleted);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Admins: sign-ups waiting for approval, shown as a badge on Users, with a
  // pop-up when a new one arrives. Ids already announced live in
  // sessionStorage so moving between pages doesn't announce them again.
  const [pending, setPending] = useState(0);
  const [newSignup, setNewSignup] = useState(null);
  useEffect(() => {
    if (!isAdmin) return undefined;
    const check = async () => {
      try {
        const { data } = await api.get('/admin/pending');
        setPending(data.count);
        let seen = null;
        try { seen = JSON.parse(sessionStorage.getItem('pending_seen') || 'null'); } catch (_) {}
        const fresh = seen ? data.users.filter((u) => !seen.includes(u.id)) : [];
        if (fresh.length) setNewSignup(fresh.length === 1 ? (fresh[0].full_name || fresh[0].email) : `${fresh.length} people`);
        try { sessionStorage.setItem('pending_seen', JSON.stringify(data.users.map((u) => u.id))); } catch (_) {}
      } catch (err) {
        // Signed in as someone else in another tab (tabs share the sign-in),
        // or no longer an admin: reload who this is, which hides Users
        if (err.response?.status === 403) refreshUser();
      }
    };
    check();
    const id = setInterval(check, PENDING_POLL_MS);
    window.addEventListener('admin:users-changed', check);
    return () => {
      clearInterval(id);
      window.removeEventListener('admin:users-changed', check);
    };
  }, [isAdmin, refreshUser]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', Icon: InsightsRoundedIcon, color: '#2563EB', tint: '#EAF1FF', path: '/dashboard' },
    { text: 'History', Icon: HistoryRoundedIcon, color: '#C2710C', tint: '#FFF3E2', path: '/history' },
    { text: 'Templates', Icon: PaletteRoundedIcon, color: '#7C3AED', tint: '#F1EBFF', path: '/templates' },
    ...(isAdmin
      ? [{ text: 'Users', Icon: ManageAccountsRoundedIcon, color: '#0F766E', tint: '#E3F6F3', path: '/admin/users', badge: pending }]
      : []),
  ];

  const formatSync = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return date.toLocaleDateString();
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
        <img
          src={MasLogo}
          alt="MAS Holdings Logo"
          style={{ width: '160px', height: 'auto' }}
        />
      </Toolbar>
      <Divider />
      <List sx={{ px: 1.5, py: 1.5 }}>
        {menuItems.map((item) => {
          const selected = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={selected}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2.5,
                  py: 0.9,
                  px: 1,
                  '&.Mui-selected, &.Mui-selected:hover': { bgcolor: '#FDECEC' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: selected ? '#fff' : item.color,
                      bgcolor: item.tint,
                      background: selected ? 'linear-gradient(135deg, #E53935, #B71C1C)' : undefined,
                      boxShadow: selected ? '0 3px 8px rgba(211, 47, 47, 0.35)' : 'none',
                    }}
                  >
                    <Badge badgeContent={item.badge || 0} color="warning" max={99}>
                      <item.Icon sx={{ fontSize: 21 }} />
                    </Badge>
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: selected ? 600 : 500, color: selected ? '#B71C1C' : '#374151' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SyncIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
            Last data sync
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
            {formatSync(lastSync)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
          background: 'linear-gradient(90deg, #757575, #9E9E9E, #BDBDBD, #D32F2F, #BDBDBD, #9E9E9E, #757575)',
          backgroundSize: '300% 100%',
          animation: 'gradientShift 8s ease infinite',
          '@keyframes gradientShift': {
            '0%': { backgroundPosition: '0% 50%' },
            '50%': { backgroundPosition: '100% 50%' },
            '100%': { backgroundPosition: '0% 50%' },
          },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.6rem' }}>
              {user?.full_name ? `Welcome back, ${user.full_name.split(' ')[0]}!` : 'Job Posting Automation'}
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={handleProfileMenuOpen}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'rgba(255, 255, 255, 0.2)', border: '2px solid rgba(255, 255, 255, 0.5)' }}>
              {user?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{user?.full_name || 'Signed in'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Snackbar
        open={!!newSignup}
        onClose={(_, reason) => reason !== 'clickaway' && setNewSignup(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setNewSignup(null)}
          action={
            <Button color="inherit" size="small" onClick={() => { setNewSignup(null); navigate('/admin/users'); }}>
              Review
            </Button>
          }
        >
          New sign-up from {newSignup} is waiting for approval.
        </Alert>
      </Snackbar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
}
